// ══════════════════════════════════════════════════════════════════════════════
// SELDOR — CommandCenterPage (Admin)
// Platform-wide metrics and activity
// ══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Activity, Users, UserCheck, Building2,
  Upload, AlertTriangle, Lightbulb, Coffee
} from 'lucide-react';
import { useTheme } from '@contexts/ThemeContext';
import { useLang } from '@contexts/LangContext';
import { supabase } from '@lib/supabase';
import { PageHeader } from '@components/ui/PageHeader';
import { Card, Grid, Badge } from '@components/ui/Primitives';
import { StatCard } from '@components/ui/StatCard';
import { LoadingSpinner } from '@components/ui/States';

export function CommandCenterPage() {
  const { theme } = useTheme();
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [tenantAlerts, setTenantAlerts] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [usersRes, activeRes, tenantsRes, insightsRes, gapsRes, salesRes] = await Promise.all([
          supabase.from('profiles').select('*', { count: 'exact', head: true }),
          supabase.from('profiles').select('*', { count: 'exact', head: true }).eq('is_active', true),
          supabase.from('tenants').select('*'),
          supabase.from('insights').select('tenant_id').eq('is_dismissed', false),
          supabase.from('financial_gaps').select('tenant_id').eq('is_resolved', false),
          supabase.from('sales_daily').select('total_orders, sale_date').order('sale_date', { ascending: false }).limit(30)
        ]);

        setStats({
          totalUsers: usersRes.count || 0,
          activeUsers: activeRes.count || 0,
          tenants: tenantsRes.data?.length || 0,
          openInsights: insightsRes.data?.length || 0,
          openGaps: gapsRes.data?.length || 0
        });

        // Tenant alerts (insights + gaps per tenant)
        const tenantMap = {};
        (insightsRes.data || []).forEach(i => {
          tenantMap[i.tenant_id] = (tenantMap[i.tenant_id] || 0) + 1;
        });
        (gapsRes.data || []).forEach(g => {
          tenantMap[g.tenant_id] = (tenantMap[g.tenant_id] || 0) + 1;
        });
        const alerts = (tenantsRes.data || []).map(t => ({
          ...t,
          alertCount: tenantMap[t.id] || 0
        })).sort((a, b) => b.alertCount - a.alertCount);
        setTenantAlerts(alerts);

        // Platform activity
        const activity = [...(salesRes.data || [])]
          .reverse()
          .map(s => ({
            date: s.sale_date?.slice(5),
            orders: s.total_orders
          }));
        setActivityData(activity);
      } catch (err) {
        console.error('[CommandCenter] Error:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <LoadingSpinner />;

  const tooltipStyle = {
    background: theme.bgCard,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    fontSize: 12,
    padding: '8px 12px'
  };

  return (
    <div>
      <PageHeader
        icon={<Activity size={22} color={theme.primary} />}
        title={t.commandCenter}
        description={lang === 'ar' ? 'مراقبة المنصة آنياً' : 'Real-time platform monitoring'}
      />

      <div style={{ marginBottom: 24 }}>
        <Grid>
          <StatCard
            icon={<Users size={20} color={theme.primary} />}
            label={t.totalUsers}
            value={stats?.totalUsers || 0}
          />
          <StatCard
            icon={<UserCheck size={20} color={theme.success} />}
            label={t.activeUsers}
            value={stats?.activeUsers || 0}
            accentColor={theme.success}
          />
          <StatCard
            icon={<Building2 size={20} color={theme.info} />}
            label={t.totalTenants}
            value={stats?.tenants || 0}
            accentColor={theme.info}
          />
          <StatCard
            icon={<Lightbulb size={20} color={theme.warning} />}
            label={t.openInsights}
            value={stats?.openInsights || 0}
            accentColor={theme.warning}
          />
          <StatCard
            icon={<AlertTriangle size={20} color={theme.danger} />}
            label={t.activeGaps}
            value={stats?.openGaps || 0}
            accentColor={theme.danger}
          />
        </Grid>
      </div>

      <Grid min={340}>
        {/* Tenants with highest alerts */}
        <Card style={{ padding: 22 }}>
          <h4 style={{
            margin: '0 0 16px',
            fontSize: 14,
            fontWeight: 700,
            color: theme.text
          }}>
            {lang === 'ar' ? 'أعلى معدلات التنبيهات' : 'Highest Alert Rates'}
          </h4>
          {tenantAlerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: theme.textTertiary, fontSize: 13 }}>
              {lang === 'ar' ? 'لا توجد بيانات' : 'No data'}
            </div>
          ) : (
            tenantAlerts.map((tn, idx) => (
              <div key={tn.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 0',
                borderBottom: idx < tenantAlerts.length - 1
                  ? `1px solid ${theme.border}`
                  : 'none'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Coffee size={16} color={theme.primary} />
                  <span style={{ fontSize: 13, color: theme.text, fontWeight: 500 }}>
                    {lang === 'ar' ? tn.name_ar : tn.name_en}
                  </span>
                </div>
                <Badge variant={tn.alertCount > 5 ? 'danger' : tn.alertCount > 0 ? 'warning' : 'success'}>
                  {tn.alertCount} {t.alerts}
                </Badge>
              </div>
            ))
          )}
        </Card>

        {/* Platform activity */}
        <Card style={{ padding: 22 }}>
          <h4 style={{
            margin: '0 0 16px',
            fontSize: 14,
            fontWeight: 700,
            color: theme.text
          }}>
            {lang === 'ar' ? 'نشاط المنصة' : 'Platform Activity'}
          </h4>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={activityData}>
              <defs>
                <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={theme.primary} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={theme.primary} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.border} />
              <XAxis
                dataKey="date"
                tick={{ fill: theme.textTertiary, fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                tick={{ fill: theme.textTertiary, fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Area
                type="monotone"
                dataKey="orders"
                stroke={theme.primary}
                fill="url(#actGrad)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </Grid>
    </div>
  );
}
